import { Home, User, Calendar, Building2, ShoppingBag } from 'lucide-react';
import { useShopStore } from '../../store/shopStore';

interface NavigationProps {
  currentScreen: string;
  onNavigate: (screen: any) => void;
}

// Five tabs, mapped onto the marketplace IA documented in
// docs/INFORMATION_ARCHITECTURE.md.
//   - Home: discovery hub
//   - Clubs: marketplace browse — clubs are the unit of supply, with
//     courts and coaches surfaced within. Court/coach detail screens
//     keep this tab lit.
//   - Bookings: your activity (machine + court + coach bookings)
//   - Shop: gear ecommerce
//   - Profile: account, addresses, favourites, help
//
// The previous nav had separate Courts and Coaches tabs; they were
// duplicating the Clubs surface and were the main reason the bar
// felt cluttered. Their detail screens still exist and are reachable
// from Home or Club detail.
const NAV = [
  { id: 'home',    icon: Home,        label: 'Home',     match: ['home'] },
  { id: 'clubs',   icon: Building2,   label: 'Clubs',    match: [
    'clubs', 'club',
    'courts', 'court', 'court-checkout',
    'coaches', 'coach', 'coach-checkout', 'package-checkout',
  ] },
  { id: 'orders',  icon: Calendar,    label: 'Bookings', match: ['orders'] },
  { id: 'shop',    icon: ShoppingBag, label: 'Shop',     match: ['shop', 'shop-collection', 'shop-product', 'shop-cart'] },
  { id: 'profile', icon: User,        label: 'Profile',  match: ['profile', 'personal-info', 'saved-addresses', 'help-support', 'notifications', 'favorites'] },
] as const;

export default function Navigation({ currentScreen, onNavigate }: NavigationProps) {
  const cartCount = useShopStore((state) => state.lines.reduce((sum, line) => sum + line.quantity, 0));

  return (
    <nav
      className="sticky bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t border-border z-40"
      aria-label="Primary"
    >
      <div className="grid grid-cols-5 max-w-2xl mx-auto h-16 px-1">
        {NAV.map((item) => {
          const Icon = item.icon;
          const isActive = (item.match as readonly string[]).includes(currentScreen);
          const showCartBadge = item.id === 'shop' && cartCount > 0;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              aria-current={isActive ? 'page' : undefined}
              className="group flex flex-col items-center justify-center gap-0.5"
            >
              {/* Bundle spec: active = Tennis Green icon + Navy label,
                  inactive = neutral grey both. Lime is reserved for
                  premium-dark surfaces, not the Court White nav. */}
              <div
                className={`relative flex items-center justify-center transition-colors ${
                  isActive ? 'text-[#1A6B4F]' : 'text-[#8B9183] group-hover:text-[#0F1E36]'
                }`}
              >
                <Icon className="w-[22px] h-[22px]" strokeWidth={isActive ? 2.25 : 1.75} />
                {showCartBadge && (
                  <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 rounded-full bg-[#1A6B4F] text-white text-[9px] flex items-center justify-center font-semibold">
                    {cartCount}
                  </span>
                )}
              </div>
              <span
                className={`text-[10px] leading-none font-semibold ${
                  isActive ? 'text-[#0F1E36]' : 'text-[#8B9183]'
                }`}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
      {/* iOS safe-area */}
      <div className="h-[env(safe-area-inset-bottom)]" />
    </nav>
  );
}
